import { Controller } from '@hotwired/stimulus'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'

// Fills in the "Entries" column on the admin ledger imports index. Each cell
// renders a spinner placeholder carrying a `data-entries-id`. We poll the
// entry_counts endpoint with the ids still pending; it returns Turbo Streams
// that replace resolved cells (dropping their `data-entries-id`). Polling stops
// once no placeholders remain. See #32238.
export default class extends Controller {
  static values = {
    url: String,
    // Counts are usually computed within a few hundred ms, so poll quickly at
    // first to resolve cells fast, then back off towards maxInterval to avoid
    // hammering for companies whose counts take longer.
    initialInterval: { type: Number, default: 1000 },
    maxInterval: { type: Number, default: 5000 },
  }

  declare readonly urlValue: string
  declare readonly initialIntervalValue: number
  declare readonly maxIntervalValue: number

  private timer: ReturnType<typeof setTimeout> | null = null
  private currentInterval = 0
  private disconnected = false

  connect(): void {
    this.disconnected = false
    this.currentInterval = this.initialIntervalValue
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    void this.poll()
  }

  disconnect(): void {
    this.disconnected = true
    this.stopPolling()
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
  }

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.stopPolling()
    } else {
      this.scheduleNextPoll()
    }
  }

  private scheduleNextPoll(): void {
    if (this.timer) return
    if (this.pendingIds.length === 0) return
    this.timer = setTimeout(() => {
      this.timer = null
      void this.poll()
    }, this.currentInterval)
    this.currentInterval = Math.min(
      this.currentInterval * 1.5,
      this.maxIntervalValue
    )
  }

  private stopPolling(): void {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private get pendingIds(): string[] {
    return [...this.element.querySelectorAll('[data-entries-id]')].map(
      (el) => (el as HTMLElement).dataset.entriesId as string
    )
  }

  private async poll(): Promise<void> {
    const ids = this.pendingIds
    if (ids.length === 0) return

    try {
      const response = await SFetch.raw(
        `${this.urlValue}?ids=${ids.join(',')}`,
        {
          headers: { Accept: 'text/vnd.turbo-stream.html' },
        }
      )

      if (this.disconnected) return

      const html = await response.text()
      this.renderStreamMessage(html)
      this.scheduleNextPoll()
    } catch (error) {
      if (this.disconnected) return

      SfLog.warn('Error polling ledger import entry counts', {
        extra: { error },
      })
      this.scheduleNextPoll()
    }
  }

  // Append turbo-stream elements to the DOM so Turbo's MutationObserver picks
  // them up, processes the replace action, and removes them. We avoid importing
  // from @hotwired/turbo directly because the package ships no TypeScript
  // declarations with the moduleResolution used here.
  private renderStreamMessage(html: string): void {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    doc.querySelectorAll('turbo-stream').forEach((el) => {
      document.body.appendChild(el)
    })
  }
}
